// src/holidayTracker/index.js  →  dist/holidayTracker.js  (global: HolidayTracker)
//
// 🏖 The admin Holiday tracker (v127.21) — People → Holiday sub-tab.
// One row per active staff member: current balance, future booked days,
// accrual still to come (2.5/month), and the projected year-end position.
// Each row expands into a WEEK-BY-WEEK projection to year end (bookings
// subtract in their week; +2.5 lands on the 1st of each month).
//
// Auth = the admin Bearer (DB.getToken) + absolute Worker URL (DB.workerUrl).
// Read-only — corrections stay in the Balances upload flow.

// The weekly-outstanding maths is SHARED (src/shared/holiday-weekly.js), not
// re-implemented here — the chart and any server-side use must never be able to
// disagree about what "outstanding" means.
import { weeklyOutstanding, carryRisk, CARRY_FLAG_DAYS } from '../shared/holiday-weekly.js';

function esc(s) {
  return (typeof window !== 'undefined' && window.escapeHtml)
    ? window.escapeHtml(s)
    : String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

function api(path) {
  const token = (typeof DB !== 'undefined' && DB.getToken) ? DB.getToken() : null;
  const base = (typeof DB !== 'undefined' && DB.workerUrl) ? DB.workerUrl() : '';
  const headers = {};
  if (token) headers['Authorization'] = 'Bearer ' + token;
  return fetch(base + path, { headers, cache: 'no-store' });
}

const state = { data: null, filter: '' };

function root() { return document.getElementById('holidayTrackerRoot'); }

// Pure: week-by-week balance walk from as_of to year end. Exported for tests.
export function weeklyProjection(row, asOf, year) {
  const out = [];
  let bal = row.current_balance;
  const end = new Date(year + '-12-31T00:00:00Z');
  let cursor = new Date(asOf + 'T00:00:00Z');
  // Step to the following Monday so weeks are clean Mon-Sun buckets.
  cursor.setUTCDate(cursor.getUTCDate() + ((8 - cursor.getUTCDay()) % 7 || 7));
  let guard = 0;
  let prev = asOf;
  while (cursor <= end && guard++ < 60) {
    const wkStart = cursor.toISOString().slice(0, 10);
    // Accrual: +2.5 for each month boundary crossed since `prev`.
    const mPrev = prev.slice(0, 7), mNow = wkStart.slice(0, 7);
    if (mNow > mPrev) {
      const months = (parseInt(mNow.slice(0, 4), 10) * 12 + parseInt(mNow.slice(5, 7), 10)) -
                     (parseInt(mPrev.slice(0, 4), 10) * 12 + parseInt(mPrev.slice(5, 7), 10));
      bal += months * 2.5;
    }
    const wkEndD = new Date(cursor); wkEndD.setUTCDate(wkEndD.getUTCDate() + 6);
    const wkEnd = wkEndD.toISOString().slice(0, 10);
    const booked = (row.booked_dates || []).filter((d) => d >= wkStart && d <= wkEnd);
    bal -= booked.length;
    out.push({ week: wkStart, booked: booked.length, balance: Math.round(bal * 10) / 10 });
    prev = wkStart;
    cursor.setUTCDate(cursor.getUTCDate() + 7);
  }
  return out;
}

// ── the weekly outstanding chart (v129.29) ──────────────────────────────────
// Matt: "a graph showing holiday outstanding per week, past and future."
//
// ONE line, because it is ONE quantity — the company's remaining holiday
// liability. It is drawn SOLID up to today and DASHED after, because the
// number's meaning does not change across today but our confidence in it does:
// a taken day is a fact, a booked day is a plan somebody can still move.
// Same colour on both halves — the entity has not changed, only its certainty.
//
// Days-per-week lives in a SEPARATE small chart below rather than sharing this
// one's axis. It is the same unit but two orders of magnitude smaller (single
// days vs hundreds outstanding), so a shared scale would flatten it into the
// baseline, and a second y-axis is the one thing a chart must never have.
//
// No external chart library — inline SVG. Hover is pure CSS (no JS handlers, so
// nothing to window-bind and nothing to leak) plus a native <title> per week,
// which also makes the values reachable by screen readers and on touch.
const CHART_W = 760, CHART_H = 200, CHART_PAD_L = 44, CHART_PAD_R = 12, CHART_PAD_T = 12, CHART_PAD_B = 22;
const BARS_H = 64;
// ELC green — the console's own brand colour, and validated against the white
// card surface (lightness band, chroma floor, 3:1 contrast: all pass).
const SERIES = '#009E3D';
// INK_3 and HAIRLINE are drawn in the SVG (chart colours, left as they are). The HTML around the chart uses the
// variables, so it turns dark with the page (v136.2): INK_2 is --tiny exactly, INK_3_CSS keeps #8A9590 in Light.
const INK_2 = 'var(--tiny)', INK_3 = '#8A9590', HAIRLINE = 'rgba(26,29,28,0.10)';
const INK_3_CSS = 'var(--ap-ink-3)';

function niceCeil(v) {
  if (!(v > 0)) return 10;
  const mag = Math.pow(10, Math.floor(Math.log10(v)));
  return Math.ceil(v / (mag / 2)) * (mag / 2);
}
function shortWeek(w) {
  const M = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return Number(w.slice(8, 10)) + ' ' + M[Number(w.slice(5, 7)) - 1];
}


// ── the carry-risk list (v129.34) ───────────────────────────────────────────
// Matt: "can it flag when they are planned to go over 31 days (so we know when
// to tell them they should book a holiday)?" The limit is CARRY_FLAG_DAYS
// (src/shared/holiday-weekly.js): 15 since 19 Oct 2026, the Holiday Policy's
// maximum carry-over. Every label below reads the constant.
//
// Rendered ABOVE the chart, not below it: this is the only part of the page
// that asks somebody to DO something today. The chart explains; this acts.
export function carryRiskHtml(risk) {
  const list = Array.isArray(risk) ? risk : [];
  if (!list.length) {
    return '<div style="border:1px solid var(--ap-ok-line);background:var(--ok-bg);border-radius:12px;padding:12px 14px;margin-bottom:14px;' +
      'font-size:13px;color:var(--ok-deep)">Nobody is on course to finish the year over ' + CARRY_FLAG_DAYS +
      ' days. Nothing to chase.</div>';
  }
  const rows = list.map(function (r) {
    return '<tr>' +
      '<td translate="no" style="padding:6px 10px;font-size:13px">' + esc(r.name || ('#' + r.staff_id)) + '</td>' +
      '<td translate="no" style="padding:6px 10px;font-size:12.5px;color:' + INK_2 + '">' + esc(r.store || '') + '</td>' +
      '<td style="padding:6px 10px;text-align:end;font-size:13px;font-variant-numeric:tabular-nums">' +
        r.projected_year_end + '</td>' +
      '<td style="padding:6px 10px;text-align:end;font-size:13px;font-weight:600;font-variant-numeric:tabular-nums">' +
        r.must_book + '</td>' +
      '<td style="padding:6px 10px;text-align:end;font-size:12.5px;color:' + INK_2 + '">' +
        (r.booked_ahead ? r.booked_ahead + ' booked' : '—') + '</td>' +
    '</tr>';
  }).join('');
  return '<div style="border:1px solid var(--ap-orange-line);background:var(--ap-orange-bg);border-radius:12px;padding:12px 14px;margin-bottom:14px">' +
    '<div style="font-weight:600;font-size:13.5px;color:var(--ap-orange-deep);margin-bottom:4px">' +
      list.length + (list.length === 1 ? ' person is' : ' people are') +
      ' on course to carry more than ' + CARRY_FLAG_DAYS + ' days into next year</div>' +
    '<div style="font-size:12px;color:var(--ap-orange-deep);line-height:1.5;margin-bottom:8px">' +
      'Projected to 31 December if they book nothing else. <b>Book</b> is the least they need to take ' +
      'between now and year end to come in under the limit — the sooner they are asked, the more of the ' +
      'year is left to take it in.' +
    '</div>' +
    '<div style="overflow-x:auto"><table style="border-collapse:collapse;width:100%;background:var(--card);border-radius:8px">' +
      '<tr style="font-size:11px;text-transform:uppercase;letter-spacing:.03em;color:' + INK_3_CSS + '">' +
        '<th style="padding:6px 10px;text-align:start">Staff</th>' +
        '<th style="padding:6px 10px;text-align:start">Store</th>' +
        '<th style="padding:6px 10px;text-align:end">Year-end</th>' +
        '<th style="padding:6px 10px;text-align:end">Book</th>' +
        '<th style="padding:6px 10px;text-align:end">Already booked</th>' +
      '</tr>' + rows +
    '</table></div></div>';
}

export function chartHtml(series) {
  const pts = Array.isArray(series) ? series : [];
  if (pts.length < 2) {
    return '<div style="padding:18px;color:' + INK_2 + ';font-size:13px">Not enough of the year on record to draw a line yet.</div>';
  }
  const innerW = CHART_W - CHART_PAD_L - CHART_PAD_R;
  const innerH = CHART_H - CHART_PAD_T - CHART_PAD_B;
  const maxY = niceCeil(Math.max.apply(null, pts.map((p) => p.outstanding)));
  // The y-axis starts at ZERO. Truncating it would exaggerate every movement in
  // a number people are going to quote at each other.
  const x = (i) => CHART_PAD_L + (innerW * i) / (pts.length - 1);
  const y = (v) => CHART_PAD_T + innerH - (innerH * Math.max(0, v)) / (maxY || 1);

  const todayIdx = Math.max(0, pts.findIndex((p) => p.state === 'current'));
  const solid = pts.slice(0, todayIdx + 1);
  const dashed = pts.slice(todayIdx);          // overlaps by one so the halves join

  const path = (arr, from) => arr.map((p, k) => (k ? 'L' : 'M') + x(from + k).toFixed(1) + ' ' + y(p.outstanding).toFixed(1)).join(' ');

  // gridlines + y labels
  let grid = '';
  for (let g = 0; g <= 4; g++) {
    const v = (maxY / 4) * g;
    const yy = y(v).toFixed(1);
    grid += '<line x1="' + CHART_PAD_L + '" x2="' + (CHART_W - CHART_PAD_R) + '" y1="' + yy + '" y2="' + yy +
      '" stroke="' + HAIRLINE + '" stroke-width="1"/>' +
      '<text x="' + (CHART_PAD_L - 6) + '" y="' + yy + '" text-anchor="end" dominant-baseline="middle" ' +
      'font-size="10" fill="' + INK_3 + '">' + Math.round(v) + '</text>';
  }

  // Month ticks along the bottom — one label per month start, not per week.
  // The FIRST bucket is the Monday on or before 1 Jan, so in most years it
  // belongs to the previous December; labelling it printed "Dec" on top of
  // "Jan". Ticks are therefore restricted to the charted year, taken from the
  // middle of the series so a part-year view still resolves it correctly.
  const chartYear = pts[Math.floor(pts.length / 2)].week.slice(0, 4);
  let ticks = '';
  let lastMonth = '';
  pts.forEach((p, i) => {
    if (p.week.slice(0, 4) !== chartYear) return;
    const m = p.week.slice(0, 7);
    if (m === lastMonth) return;
    lastMonth = m;
    ticks += '<text x="' + x(i).toFixed(1) + '" y="' + (CHART_H - 6) + '" text-anchor="middle" font-size="10" fill="' + INK_3 + '">' +
      ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][Number(m.slice(5, 7)) - 1] + '</text>';
  });

  // hover: one transparent column per week; CSS reveals its crosshair.
  // <title> carries the numbers, so the values are reachable without a mouse.
  const colW = innerW / (pts.length - 1);
  const hover = pts.map((p, i) =>
    '<g class="hkw">' +
    '<line class="hkx" x1="' + x(i).toFixed(1) + '" x2="' + x(i).toFixed(1) + '" y1="' + CHART_PAD_T + '" y2="' + (CHART_PAD_T + innerH) +
      '" stroke="' + SERIES + '" stroke-width="1" opacity="0"/>' +
    '<circle class="hkd" cx="' + x(i).toFixed(1) + '" cy="' + y(p.outstanding).toFixed(1) + '" r="4" fill="' + SERIES +
      '" style="stroke:var(--card)" stroke-width="2" opacity="0"/>' +
    '<rect x="' + (x(i) - colW / 2).toFixed(1) + '" y="' + CHART_PAD_T + '" width="' + colW.toFixed(1) + '" height="' + innerH +
      '" fill="transparent"><title>w/c ' + shortWeek(p.week) + '\n' + p.outstanding + ' days outstanding\n' +
      p.days + ' day(s) ' + (p.state === 'future' ? 'booked' : 'taken') + ' that week</title></rect>' +
    '</g>').join('');

  // the today rule + the three labels worth showing (never one per point)
  const todayX = x(todayIdx).toFixed(1);
  const first = pts[0], now = pts[todayIdx], last = pts[pts.length - 1];

  const line =
    '<svg viewBox="0 0 ' + CHART_W + ' ' + CHART_H + '" width="100%" role="img" ' +
      'aria-label="Holiday days outstanding for the company, week by week across the year" ' +
      'style="display:block;overflow:visible">' +
    grid + ticks +
    '<line x1="' + todayX + '" x2="' + todayX + '" y1="' + CHART_PAD_T + '" y2="' + (CHART_PAD_T + innerH) +
      '" stroke="' + INK_3 + '" stroke-width="1" stroke-dasharray="2 3"/>' +
    '<text x="' + todayX + '" y="' + (CHART_PAD_T - 2) + '" text-anchor="middle" font-size="10" fill="' + INK_3 + '">today</text>' +
    '<path d="' + path(dashed, todayIdx) + '" fill="none" stroke="' + SERIES + '" stroke-width="2" ' +
      'stroke-dasharray="5 4" stroke-linecap="round" opacity="0.75"/>' +
    '<path d="' + path(solid, 0) + '" fill="none" stroke="' + SERIES + '" stroke-width="2" stroke-linecap="round"/>' +
    '<circle cx="' + todayX + '" cy="' + y(now.outstanding).toFixed(1) + '" r="4.5" fill="' + SERIES + '" style="stroke:var(--card)" stroke-width="2"/>' +
    hover +
    '</svg>';

  // the small companion chart — days actually going out each week
  const maxDays = Math.max(1, Math.max.apply(null, pts.map((p) => p.days)));
  const bw = Math.max(2, colW - 2);            // 2px surface gap between bars
  const bars = pts.map((p, i) => {
    const h = (BARS_H - 16) * (p.days / maxDays);
    return '<rect x="' + (x(i) - bw / 2).toFixed(1) + '" y="' + (BARS_H - 12 - h).toFixed(1) + '" width="' + bw.toFixed(1) +
      '" height="' + h.toFixed(1) + '" rx="' + Math.min(2, bw / 2).toFixed(1) + '" fill="' + SERIES + '" opacity="' +
      (p.state === 'future' ? '0.35' : '0.75') + '"><title>w/c ' + shortWeek(p.week) + '\n' + p.days + ' day(s) ' +
      (p.state === 'future' ? 'booked' : 'taken') + '</title></rect>';
  }).join('');
  const barsSvg =
    '<svg viewBox="0 0 ' + CHART_W + ' ' + BARS_H + '" width="100%" role="img" ' +
      'aria-label="Holiday days taken or booked each week" style="display:block">' +
    '<line x1="' + CHART_PAD_L + '" x2="' + (CHART_W - CHART_PAD_R) + '" y1="' + (BARS_H - 12) + '" y2="' + (BARS_H - 12) +
      '" stroke="' + HAIRLINE + '" stroke-width="1"/>' +
    '<text x="' + (CHART_PAD_L - 6) + '" y="' + (BARS_H - 12) + '" text-anchor="end" dominant-baseline="middle" font-size="10" fill="' + INK_3 + '">0</text>' +
    '<text x="' + (CHART_PAD_L - 6) + '" y="14" text-anchor="end" dominant-baseline="middle" font-size="10" fill="' + INK_3 + '">' + maxDays + '</text>' +
    bars + '</svg>';

  // the numbers, for anyone who would rather read than hover (and for print)
  const table = '<details style="margin-top:10px"><summary style="cursor:pointer;font-size:12px;color:var(--info-deep)">Show the weekly numbers</summary>' +
    '<div style="overflow-x:auto;margin-top:8px"><table style="border-collapse:collapse;font-size:11.5px">' +
    '<tr style="color:' + INK_2 + ';text-align:end"><th style="padding:2px 10px;text-align:start">Week</th>' +
    '<th style="padding:2px 10px">Days out</th><th style="padding:2px 10px">Outstanding</th></tr>' +
    pts.map((p) => '<tr' + (p.state === 'future' ? ' style="color:' + INK_3_CSS + '"' : '') + '>' +
      '<td style="padding:2px 10px">w/c ' + esc(p.week) + (p.state === 'current' ? ' (now)' : '') + '</td>' +
      '<td style="padding:2px 10px;text-align:end">' + p.days + '</td>' +
      '<td style="padding:2px 10px;text-align:end">' + p.outstanding + '</td></tr>').join('') +
    '</table></div></details>';

  return '<style>.hkw rect{cursor:crosshair}.hkw:hover .hkx,.hkw:hover .hkd{opacity:1}</style>' +
    '<div style="border:1px solid var(--border);border-radius:12px;padding:14px 12px 8px;background:var(--card)">' +
    '<div style="display:flex;align-items:baseline;gap:12px;flex-wrap:wrap;padding:0 4px 4px">' +
      '<div style="font-weight:600;font-size:14px">Holiday days outstanding</div>' +
      '<div style="font-size:12px;color:' + INK_2 + '">solid = taken · dashed = booked ahead</div>' +
    '</div>' +
    '<div style="display:flex;gap:20px;flex-wrap:wrap;padding:2px 4px 10px;font-variant-numeric:tabular-nums">' +
      '<div><div style="font-size:10.5px;text-transform:uppercase;letter-spacing:.04em;color:' + INK_3_CSS + '">Start of year</div>' +
        '<div style="font-size:18px;font-weight:600">' + first.outstanding + '</div></div>' +
      '<div><div style="font-size:10.5px;text-transform:uppercase;letter-spacing:.04em;color:' + INK_3_CSS + '">Outstanding now</div>' +
        '<div style="font-size:18px;font-weight:600;color:' + SERIES + '">' + now.outstanding + '</div></div>' +
      '<div><div style="font-size:10.5px;text-transform:uppercase;letter-spacing:.04em;color:' + INK_3_CSS + '">After everything booked</div>' +
        '<div style="font-size:18px;font-weight:600">' + last.outstanding + '</div></div>' +
    '</div>' +
    // Both SVGs live inside ONE horizontal scroller with a min-width, so they
    // stay aligned with each other and the axis labels stay legible on a phone.
    // Scaling a 53-week axis down to a 340px screen renders the month labels at
    // about 4px — technically present, actually unreadable. Scroll, don't shrink.
    '<div style="overflow-x:auto;-webkit-overflow-scrolling:touch"><div style="min-width:620px">' +
    line +
    '<div style="font-size:11px;color:' + INK_3_CSS + ';padding:6px 4px 2px">Days taken or booked each week</div>' +
    barsSvg +
    '</div></div>' +
    '<div style="font-size:11.5px;color:' + INK_2 + ';line-height:1.5;padding:8px 4px 0">' +
      'Outstanding = what everyone has EARNED by that week (2.5 days a month each) minus every holiday day ' +
      'dated on or before it. That is why the line climbs: the liability grows as people accrue, and drops ' +
      'when they take days off. It matches the <b>Current</b> column below at today\'s point.' +
    '</div>' +
    '<div style="font-size:11.5px;color:var(--ap-orange-deep);background:var(--ap-orange-bg);border:1px solid var(--ap-orange-line);border-radius:8px;padding:8px 10px;margin:8px 4px 6px;line-height:1.5">' +
      'Accrual is projected from each person\'s last balance reconcile, because Shift does not hold a start date. ' +
      'For anyone whose reconcile predates their joining, the early weeks are slightly generous. ' +
      'Amanah has Joining Date — importing it would make the past exact.' +
    '</div>' +
    table +
    '</div>';
}

function rowHtml(r, asOf, year) {
  const balCol = r.current_balance < 0 ? 'var(--ui-bad)' : r.current_balance <= 2 ? 'var(--ui-warn)' : 'var(--text)';
  const projCol = r.projected_year_end < 0 ? 'var(--ui-bad)' : 'var(--text)';
  const weeks = weeklyProjection(r, asOf, year);
  const weekRows = weeks.map((w) =>
    '<tr><td style="padding:3px 10px;font-size:11.5px;color:var(--tiny)">w/c ' + esc(w.week) + '</td>' +
    '<td style="padding:3px 10px;font-size:11.5px;text-align:center">' + (w.booked ? '🏖 ' + w.booked : '') + '</td>' +
    '<td style="padding:3px 10px;font-size:11.5px;text-align:end;font-weight:600;color:' + (w.balance < 0 ? 'var(--ui-bad)' : 'var(--text)') + '">' + w.balance + '</td></tr>').join('');
  return '<tr>' +
    '<td translate="no" style="padding:8px 10px;font-weight:600;white-space:nowrap">' + esc(r.name) +
      '<div style="font-size:11px;color:var(--tiny);font-weight:400">' + esc(r.store) + (r.emp_code ? ' · ' + esc(r.emp_code) : '') + '</div></td>' +
    '<td style="padding:8px 10px;text-align:end;font-weight:700;color:' + balCol + '">' + r.current_balance + '</td>' +
    '<td style="padding:8px 10px;text-align:end">' + r.taken_to_date + '</td>' +
    '<td style="padding:8px 10px;text-align:end">' + (r.booked_future || '') + '</td>' +
    '<td style="padding:8px 10px;text-align:end;color:var(--tiny)">+' + r.accrue_rest + '</td>' +
    '<td style="padding:8px 10px;text-align:end">' + r.future_no_booked + '</td>' +
    '<td style="padding:8px 10px;text-align:end;font-weight:700;color:' + projCol + '">' + r.projected_year_end + '</td>' +
    '<td style="padding:8px 10px">' +
      '<details><summary style="cursor:pointer;font-size:11.5px;color:var(--info-deep)">weekly</summary>' +
      '<table style="border-collapse:collapse;margin-top:4px"><tr style="font-size:10.5px;color:var(--tiny);text-transform:uppercase">' +
      '<th style="padding:2px 10px;text-align:start">Week</th><th style="padding:2px 10px">Booked</th><th style="padding:2px 10px;text-align:end">Balance</th></tr>' +
      weekRows + '</table></details></td>' +
    '</tr>';
}

function paint() {
  const el = root(); if (!el) return;
  const d = state.data;
  if (!d) { el.innerHTML = '<div style="padding:20px;color:var(--tiny)">Loading holiday balances…</div>'; return; }
  if (!d.ok) { el.innerHTML = '<div style="padding:16px;border:1px solid var(--ap-danger-line);background:var(--danger-bg);border-radius:10px;color:var(--danger-deep)">Could not load the tracker — <span translate="no">' + esc(d.error || 'unknown') + '</span></div>'; return; }
  const q = state.filter.toLowerCase();
  const rows = (d.staff || []).filter((r) => !q ||
    String(r.name).toLowerCase().indexOf(q) >= 0 || String(r.store).toLowerCase().indexOf(q) >= 0);
  const flagged = rows.filter((r) => r.current_balance < 0 || r.projected_year_end < 0).length;
  el.innerHTML =
    '<div style="display:flex;gap:10px;align-items:center;flex-wrap:wrap;margin-bottom:10px">' +
      '<input placeholder="🔍 Filter by name / store" value="' + esc(state.filter) + '" oninput="HolidayTracker.setFilter(this.value)" style="flex:1;min-width:220px;border:1.5px solid var(--border);border-radius:10px;padding:9px 12px;font-size:13px;font-family:inherit">' +
      '<button class="btn btn-outline" onclick="HolidayTracker.render(true)">🔄 Refresh</button>' +
    '</div>' +
    '<div style="font-size:12px;color:var(--tiny);margin-bottom:10px">Everyone accrues <b>' + d.accrual_per_month + ' days a month</b> (credited at month end). ' +
      '"Current" = the HR-anchored balance minus days taken; "If nothing else booked" adds the rest of the year\'s accrual; "Year-end" also subtracts future booked days. As of ' + esc(d.as_of) + '.' +
      (flagged ? ' <b style="color:var(--ui-warn)">' + flagged + ' flagged (negative now or at year end).</b>' : '') + '</div>' +
    // v129.29: the chart is drawn from the SAME filtered rows as the table, so
    // typing a store name re-draws the line for that store. One filter, one
    // subject — a chart showing the company while the table below it shows one
    // store would be read as the same thing.
    carryRiskHtml(carryRisk(rows, d.year)) +
    '<div style="margin-bottom:14px">' + chartHtml(weeklyOutstanding(rows, d.year, d.as_of)) + '</div>' +
    '<div style="overflow-x:auto;border:1px solid var(--border);border-radius:10px"><table style="border-collapse:collapse;width:100%;background:var(--card)">' +
    '<tr style="text-align:end;font-size:11px;color:var(--tiny);text-transform:uppercase;letter-spacing:.03em">' +
      '<th style="padding:8px 10px;text-align:start">Staff</th><th style="padding:8px 10px">Current</th><th style="padding:8px 10px">Taken</th>' +
      '<th style="padding:8px 10px">Booked ahead</th><th style="padding:8px 10px">Accrues</th><th style="padding:8px 10px">If nothing else booked</th>' +
      '<th style="padding:8px 10px">Year-end</th><th style="padding:8px 10px;text-align:start">Weeks</th></tr>' +
    rows.map((r) => rowHtml(r, d.as_of, d.year)).join('') +
    '</table></div>';
}

export async function render(force) {
  const el = root(); if (!el) return;
  if (state.data && !force) { paint(); return; }
  state.data = null; paint();
  try {
    const res = await api('/api/admin/holiday-tracker');
    state.data = res.ok ? await res.json() : { ok: false, error: 'HTTP ' + res.status };
  } catch (e) { state.data = { ok: false, error: (e && e.message) || String(e) }; }
  paint();
}

export function setFilter(v) { state.filter = String(v || ''); paint(); }
